/**
 * StudentEnrollmentsTab.jsx
 * Kullanicinin kayitli kurslarini listeler.
 * ActionMenu: Sil | Manuel Tamamla
 * Header button: Yeni Ders Ata (modal)
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Plus, X, PlayCircle } from 'lucide-react';
import VideoProgressModal from './VideoProgressModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { ActionMenu, StatusBadge, EmptyState, ConfirmDialog, RichSelect } from '../../components/admin';
import PaymentPlanFields from '../../components/sales/PaymentPlanFields';
import { useCourses } from '../../api/queries/courses';
import { useAdminEnrollUser } from '../../api/queries/students';
import { useAccountingBanks } from '../../api/queries/accounting';
// FAZ 1 satış sadeleştirme (2026-09-25) — satış anında POS hesabı tahsilat
// hesabı olarak KABUL EDİLMEZ (satis_pos_hesabi_gecersiz), yalnızca kasa/
// banka listelenir. bkz. lib/posNetAmount.js dosya başı notu.
import { isPosAccountType } from '../../lib/posNetAmount';

const STATUS_VARIANT = {
  active: 'success',
  completed: 'info',
  paused: 'warning',
  cancelled: 'danger',
};

// FAZ 1 karma ödeme turu (2026-09-25) — eski üçlü seçim (Nakit / Taksitli /
// Ücretsiz) ikiye indi: "paid" (Peşinat + Kalan, bkz. PaymentPlanFields.jsx)
// ve "free". Peşin tam tahsil / taksitli senaryoların ikisi de artık
// PaymentPlanFields'in Peşinat+Kalan sözleşmesiyle temsil edilir.
const PAYMENT_DEFAULTS = {
  method: 'paid',
  discount_percent: 0,
  notes: '',
};

export default function StudentEnrollmentsTab({ detail, enrollDelete, enrollComplete, userId, onRefresh }) {
  const { t, lang } = useLanguage();
  const toast = useToast();

  const enrollments = detail?.enrollments || [];

  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [payment, setPayment] = useState(PAYMENT_DEFAULTS);
  // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları/doğrulaması
  // artık paylaşılan components/sales/PaymentPlanFields.jsx tarafından
  // yönetilir (bkz. o dosyanın başı yorumu).
  const [paymentPlanFields, setPaymentPlanFields] = useState({});
  const [paymentPlanValid, setPaymentPlanValid] = useState(false);
  const [paymentPlanResetKey, setPaymentPlanResetKey] = useState(0);

  const { data: allCourses = [], isLoading: coursesLoading } = useCourses();
  const enrollUser = useAdminEnrollUser(userId);
  // Bu sekme yalnızca /admin/students/:userId (admin/superadmin, RoleRoute)
  // altında render edilir — GET /admin/banks require_admin, her zaman açık.
  const { data: banks = [], isLoading: banksLoading } = useAccountingBanks();
  const collectionBanks = banks.filter((b) => !isPosAccountType(b.account_type));

  const enrolledCourseIds = new Set(enrollments.map((e) => e.course_id));
  const courseOptions = allCourses
    .filter((c) => !enrolledCourseIds.has(c.course_id))
    .map((c) => ({
      value: c.course_id,
      label: c.title || c.title_tr || c.course_id,
      sublabel: c.category,
    }));
  // PaymentPlanFields'e giden toplam tutar (indirim uygulanmış) için seçili
  // kursun liste fiyatı.
  const selectedCourse = allCourses.find((c) => c.course_id === selectedCourseId);
  // FAZ 1 düzeltme turu — madde 6 (küçükler): "öğrenci zaten kayıtlı olan
  // kursu seçerse uyar" — burada gerekmiyor, `courseOptions` (yukarıda)
  // `enrolledCourseIds` ile zaten kayıtlı kursları RichSelect'ten ÇIKARIYOR;
  // aynı kursu ikinci kez seçmek bu formda zaten MÜMKÜN DEĞİL.

  const closeAssignModal = () => {
    setAssignOpen(false);
    setSelectedCourseId('');
    setPayment(PAYMENT_DEFAULTS);
    setPaymentPlanFields({});
    setPaymentPlanValid(false);
    setPaymentPlanResetKey((k) => k + 1);
  };

  // İndirim UYGULANMIŞ tutar — PaymentPlanFields'e bu gider (Peşinat/Kalan
  // hesapları indirimden SONRAKİ toplam üzerinden yapılmalı).
  const discountedTotal = selectedCourse
    ? (selectedCourse.price || 0) * (1 - (payment.discount_percent || 0) / 100)
    : 0;

  const isPaymentValid = () => {
    if (!selectedCourseId) return false;
    if (
      !Number.isFinite(payment.discount_percent) ||
      payment.discount_percent < 0 ||
      payment.discount_percent > 100
    ) {
      return false;
    }
    if (payment.method === 'paid' && !paymentPlanValid) return false;
    return true;
  };

  const handleAssign = async () => {
    if (!isPaymentValid()) return;
    const isFree = payment.method === 'free';
    try {
      await enrollUser.mutateAsync({
        courses: [{ course_id: selectedCourseId }],
        discount_percent: isFree ? 0 : payment.discount_percent,
        notes: payment.notes || '',
        // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları
        // (down_payment, down_payment_method/bank_id, installments/
        // first_due_date VEYA installment_plan, payment_method)
        // PaymentPlanFields tarafından hesaplanır. "Ücretsiz" seçiliyse
        // bunların hiçbiri gönderilmez, yalnızca payment_method="free".
        ...(isFree ? { payment_method: 'free' } : paymentPlanFields),
      });
      toast.success(t('admin.studentHub.assign_course_success'));
      closeAssignModal();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [videoProgressTarget, setVideoProgressTarget] = useState(null); // { course_id }

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await enrollDelete.mutateAsync({ userId, courseId: deleteTarget.course_id });
      toast.success(t('admin.studentHub.enroll_action_delete') + ' OK');
      setDeleteTarget(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleComplete = async () => {
    if (!completeTarget) return;
    try {
      await enrollComplete.mutateAsync({ userId, courseId: completeTarget.course_id });
      toast.success(t('admin.studentHub.enroll_action_complete') + ' OK');
      setCompleteTarget(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
          {enrollments.length} {lang === 'tr' ? 'kayıt' : 'enrollments'}
        </span>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setAssignOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white text-xs font-bold"
          data-testid="assign-course-btn"
        >
          <Plus size={14} />
          {t('admin.studentHub.btn_assign_course')}
        </motion.button>
      </div>

      {enrollments.length === 0 ? (
        <EmptyState title={lang === 'tr' ? 'Kayıt yok' : 'No enrollments'} icon={<BookOpen size={40} strokeWidth={1.5} />} />
      ) : (
      <div className="space-y-2">
        {enrollments.map((enroll, i) => (
          <div
            key={enroll.course_id || i}
            className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50"
            data-testid={`enroll-row-${i}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">
                {enroll.course_title || enroll.title || enroll.course_id}
              </p>
              {typeof enroll.progress === 'number' && (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-full bg-[var(--brand-primary)] rounded-full"
                        style={{ width: `${enroll.progress}%` }}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-[var(--text-muted)]">{enroll.progress}%</span>
                  </div>
                </div>
              )}
            </div>

            <StatusBadge
              variant={STATUS_VARIANT[enroll.status] || 'neutral'}
              label={lang === 'tr'
                ? { active: 'Aktif', completed: 'Tamamlandı', paused: 'Duraklatıldı', cancelled: 'İptal' }[enroll.status] || enroll.status
                : enroll.status || '-'}
              dot
            />

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setVideoProgressTarget(enroll)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg border-2 border-cyan-300 bg-cyan-50 text-cyan-700 text-[11px] font-bold hover:bg-cyan-100 shrink-0"
              data-testid={`video-progress-btn-${i}`}
              title={t('admin.studentHub.videoProgress.openBtn')}
            >
              <PlayCircle size={13} />
              {t('admin.studentHub.videoProgress.openBtn')}
            </motion.button>

            <ActionMenu
              items={[
                {
                  label: t('admin.studentHub.enroll_action_complete'),
                  onClick: () => setCompleteTarget(enroll),
                  disabled: enroll.status === 'completed',
                },
                {
                  label: t('admin.studentHub.enroll_action_delete'),
                  onClick: () => setDeleteTarget(enroll),
                  variant: 'destructive',
                },
              ]}
            />
          </div>
        ))}
      </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title={t('admin.studentHub.enroll_delete_title')}
        description={`${t('admin.studentHub.enroll_delete_desc')} - ${deleteTarget?.course_title || deleteTarget?.course_id}`}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleDelete}
        loading={enrollDelete.isPending}
      />

      <ConfirmDialog
        open={!!completeTarget}
        onOpenChange={(v) => !v && setCompleteTarget(null)}
        title={t('admin.studentHub.enroll_complete_title')}
        description={`${t('admin.studentHub.enroll_complete_desc')} - ${completeTarget?.course_title || completeTarget?.course_id}`}
        variant="info"
        confirmLabel={t('admin.studentHub.enroll_action_complete')}
        onConfirm={handleComplete}
        loading={enrollComplete.isPending}
      />

      <VideoProgressModal
        studentUserId={userId}
        courseId={videoProgressTarget?.course_id}
        open={!!videoProgressTarget}
        onClose={() => setVideoProgressTarget(null)}
      />

      <AnimatePresence>
        {assignOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !enrollUser.isPending && closeAssignModal()}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="assign-course-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-bold text-[var(--text-main)]">
                  {t('admin.studentHub.assign_course_title')}
                </h3>
                <button
                  onClick={closeAssignModal}
                  disabled={enrollUser.isPending}
                  className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                >
                  <X size={16} />
                </button>
              </div>

              <label className="block text-xs font-bold text-[var(--text-muted)] mb-1.5">
                {t('admin.studentHub.assign_course_label')}
              </label>
              <RichSelect
                options={courseOptions}
                value={selectedCourseId}
                onChange={setSelectedCourseId}
                placeholder={coursesLoading
                  ? (lang === 'tr' ? 'Yükleniyor...' : 'Loading...')
                  : t('admin.studentHub.assign_course_placeholder')}
                disabled={coursesLoading || enrollUser.isPending}
                className="mb-4"
              />

              <label className="block text-xs font-bold text-[var(--text-muted)] mb-1.5">
                {t('admin.studentHub.assign_course_payment_method')}
              </label>
              <select
                value={payment.method}
                onChange={(e) => setPayment((p) => ({ ...p, method: e.target.value }))}
                disabled={enrollUser.isPending}
                className="w-full mb-4 px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold bg-white focus:outline-none focus:border-[var(--brand-primary)] disabled:opacity-40"
                data-testid="assign-course-payment-method"
              >
                <option value="paid">{t('admin.studentHub.assign_course_method_paid')}</option>
                <option value="free">{t('admin.studentHub.assign_course_method_free')}</option>
              </select>

              {payment.method !== 'free' && (
                <div className="mb-4">
                  <label className="block text-xs font-bold text-[var(--text-muted)] mb-1.5">
                    {t('admin.studentHub.assign_course_discount')}
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={payment.discount_percent}
                    onChange={(e) =>
                      setPayment((p) => ({
                        ...p,
                        discount_percent: e.target.value === '' ? 0 : Number(e.target.value),
                      }))
                    }
                    disabled={enrollUser.isPending}
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold focus:outline-none focus:border-[var(--brand-primary)] disabled:opacity-40"
                    data-testid="assign-course-discount"
                  />
                </div>
              )}

              {/* FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
                  components/sales/PaymentPlanFields.jsx dosya başı yorumu. */}
              {payment.method === 'paid' && (
                <div className="mb-4">
                  <PaymentPlanFields
                    key={paymentPlanResetKey}
                    role="admin"
                    totalAmount={discountedTotal}
                    banks={collectionBanks}
                    banksLoading={banksLoading}
                    disabled={enrollUser.isPending}
                    onChange={(fields, meta) => { setPaymentPlanFields(fields); setPaymentPlanValid(meta.isValid); }}
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs font-bold text-[var(--text-muted)] mb-1.5">
                  {t('admin.studentHub.assign_course_notes')}
                </label>
                <textarea
                  value={payment.notes}
                  onChange={(e) => setPayment((p) => ({ ...p, notes: e.target.value }))}
                  disabled={enrollUser.isPending}
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm focus:outline-none focus:border-[var(--brand-primary)] disabled:opacity-40 resize-none"
                  data-testid="assign-course-notes"
                />
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={closeAssignModal}
                  disabled={enrollUser.isPending}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAssign}
                  disabled={!isPaymentValid() || enrollUser.isPending}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                  data-testid="assign-course-confirm-btn"
                >
                  {enrollUser.isPending ? '...' : t('admin.studentHub.btn_assign_course')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
